import { match } from "ts-pattern";
import type { TestSignalLogItem, TestSignalLogOutcome } from "@visioni/a3s-monitoring/app";

/**
 * How a log row reads in the table, and how it should be coloured.
 *
 * Kept apart from the component so the decisions that actually matter — what
 * each outcome means to an operator, and which states count as trouble — are
 * unit-testable. `apps/a3s-app` has no render-test infrastructure; the same
 * split is why `access.ts`, `actions.ts` and `cooldown.ts` exist.
 */
export type TestSignalLogTone = "positive" | "pending" | "negative" | "neutral";

/** One labelled status line on a row — `Cloud: …`, `Delivery: …`. */
export interface TestSignalLogStatus {
  readonly label: string;
  readonly text: string;
  readonly tone: TestSignalLogTone;
}

export interface TestSignalLogRow {
  /** Stable across pages: `(occurredAt, recordId)` is unique per camera. */
  readonly key: string;
  readonly occurredAt: string;
  /** What the entry is — "Command" or "Signal". */
  readonly entry: string;
  readonly signalPath: string;
  /**
   * The row's verdict, one line per stage.
   *
   * A signal gets two, because collapsing them misleads. Every signal row that
   * exists at all proves the clip reached a3s cloud — intake only writes one
   * once it has the clip — so "Dropped — asset not monitored" as a single red
   * line reads as a broken camera, when the camera, the edge agent and the
   * upload all worked and only the ARC leg was (correctly) skipped.
   */
  readonly statuses: readonly TestSignalLogStatus[];
  /** Secondary line: who sent it, how long it took, how many attempts failed. */
  readonly detail: string | null;
}

/** Human names for the delivery legs. `camera` and `va` mean nothing to a reader. */
const SIGNAL_PATH_LABELS: Record<string, string> = {
  camera: "Camera",
  va: "VA",
};

/** Human names for the dispatch providers a signal can be delivered to. */
const PROVIDER_LABELS: Record<string, string> = {
  visioni_v1: "Vision Patriot",
  visioni_v1_shadow: "Vision Patriot (shadow)",
  visioni_v2: "Vision Patriot",
  code9: "Code9 Patriot",
};

/**
 * What the cloud half says.
 *
 * Every signal row means a3s received the clip — intake only writes one once it
 * has the clip — so this is "Received by a3s" for all but one outcome. Stating
 * it is what tells an operator that a dropped delivery is not a broken camera.
 *
 * `duplicate` is the exception, and belongs here rather than on the delivery
 * line: recognising a repeat is something *intake* did on arrival, not a
 * verdict about the ARC leg.
 */
function cloudStatus(outcome: TestSignalLogOutcome): TestSignalLogStatus {
  const text = outcome === "duplicate" ? "Received by a3s — already seen" : "Received by a3s";
  return { label: "Cloud", text, tone: "positive" };
}

/**
 * What each outcome means for the delivery leg, in an operator's words.
 *
 * Exhaustive over `TestSignalLogOutcome` — `satisfies` makes a new outcome on
 * the wire contract fail to compile here rather than rendering a raw slug.
 *
 * Tone answers "must someone act?", not "did delivery happen?".
 *
 * Two drops are green, because they are the ones a3s chose for an asset whose
 * monitoring is working. `dropped-unintegrated-provider`: the provider's
 * operators are granted a3s app access and monitor there, so the clip reaching
 * a3s is the whole job. `dropped-non-v2-provider`: only `visioni_v2` can log a
 * probe to Patriot without raising an Activation, so a test signal is never
 * pushed to an external ARC — a real alarm from the same camera still is.
 *
 * `dropped-unmonitored-asset` stays neutral by contrast: nobody is watching
 * that asset at all, which is allowed but is not something working. Only the
 * outcomes that mean something is misconfigured or broken are red.
 */
const DELIVERY = {
  // Unreachable on a signal row — `sent` is the command's own outcome — but
  // kept so this map stays exhaustive over the shared outcome union.
  sent: { text: "Not started", tone: "pending" },
  accepted: { text: "Awaiting delivery", tone: "pending" },
  deferred: { text: "Retrying delivery", tone: "pending" },
  delivered: { text: "Delivered", tone: "positive" },
  // The cloud line carries the "already seen" part; nothing was attempted here.
  duplicate: { text: "Not attempted", tone: "neutral" },
  "dropped-no-provider": { text: "Not delivered — no monitoring provider", tone: "negative" },
  "dropped-unmonitored-asset": { text: "Not delivered — asset not monitored", tone: "neutral" },
  "dropped-missing-context": { text: "Not delivered — incomplete monitoring setup", tone: "negative" },
  "dropped-unintegrated-provider": {
    text: "Not required — unintegrated provider with a3s app access",
    tone: "positive",
  },
  "dropped-non-v2-provider": { text: "Not delivered — external provider", tone: "positive" },
  poisoned: { text: "Delivery failed", tone: "negative" },
  expired: { text: "Expired before delivery", tone: "negative" },
} as const satisfies Record<TestSignalLogOutcome, { text: string; tone: TestSignalLogTone }>;

/**
 * A delivered signal names where it landed, because "Delivered" alone does not
 * answer the question the operator opened the page with.
 */
function deliveryStatus(outcome: TestSignalLogOutcome, provider: string | undefined): TestSignalLogStatus {
  const { text, tone } = DELIVERY[outcome];
  const named =
    outcome === "delivered" && provider !== undefined ? `Delivered to ${PROVIDER_LABELS[provider] ?? provider}` : text;
  return { label: "Delivery", text: named, tone };
}

/**
 * What produced the signal. An unlabelled row leaves the reader inferring, and
 * the periodic label also explains why that row has no command above it.
 *
 * Deliberately not "daily" — the cadence is the edge rule's to choose, and this
 * label should not have to change when it does.
 */
const TRIGGER_LABELS = {
  manual: "Manually triggered signal",
  periodic: "Automatic periodic signal",
} as const;

function signalDetail(item: Extract<TestSignalLogItem, { kind: "signal" }>): string | null {
  const parts: string[] = [];
  if (item.triggerType !== undefined) parts.push(TRIGGER_LABELS[item.triggerType]);
  // Intake accepting the signal to the ARC acknowledging it — the dispatch leg
  // only, excluding the camera, the edge upload and the wait for S3 to trigger
  // intake. Naming both ends stops it reading as time since the button press.
  if (item.latencyMs !== undefined) {
    parts.push(`Delivered ${(item.latencyMs / 1000).toFixed(1)}s after reaching a3s`);
  }
  if (item.failureCount !== undefined && item.failureCount > 0) {
    parts.push(`${String(item.failureCount)} failed attempt${item.failureCount === 1 ? "" : "s"}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Project one wire item onto its table row.
 *
 * Every kind is handled explicitly and the match is `.exhaustive()`, so a third
 * kind added to `TestSignalLogItem` fails to compile here rather than falling
 * through to whichever branch happened to be last — and if one somehow reaches
 * this at runtime, ts-pattern throws `NonExhaustiveError` rather than rendering
 * it as something it is not. Mirrors the server-side projection in
 * `client-api/test-signals/list-handler.ts`.
 */
export function toLogRow(item: TestSignalLogItem): TestSignalLogRow {
  const common = {
    key: `${item.occurredAt}#${item.recordId}`,
    occurredAt: item.occurredAt,
    signalPath: SIGNAL_PATH_LABELS[item.signalPath] ?? item.signalPath,
  };

  return match(item)
    .with({ kind: "command" }, (command) => ({
      ...common,
      entry: "Command",
      // A command has one stage: it was published to the device. Everything
      // after that belongs to the signal row it produces.
      statuses: [{ label: "Status", text: "Sent to the camera", tone: "pending" as const }],
      // The raw `UserId` when the server could not resolve an address.
      detail: `Sent by ${command.actorEmail ?? command.actor}`,
    }))
    .with({ kind: "signal" }, (signal) => ({
      ...common,
      entry: "Signal",
      statuses: [cloudStatus(signal.outcome), deliveryStatus(signal.outcome, signal.provider)],
      detail: signalDetail(signal),
    }))
    .exhaustive();
}

export const EMPTY_LOG_MESSAGE = "No test signals recorded for this camera yet.";
